import React, {useContext, useEffect, useRef} from 'react';
import {Grid} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {FormProvider} from 'react-hook-form';
import Joi from 'joi';
import FormNavigation, {
    defaultBack,
    defaultNext,
} from 'components/forms/context/FormNavigation';
import {StateSaveInterceptor, LoopContext} from 'components/forms/context';
import useFormMethods from 'components/forms/hooks/useFormMethods';
import useFormPersistence from 'components/forms/hooks/useFormPersistence';
import {FormState} from 'components/forms/context';
import {getCurrentState} from 'components/forms/utils/state';
import {useFormLoopPrefix} from 'components/forms/hooks';

const useStyles = makeStyles(theme => ({
    root: {
        margin: theme.spacing(1),
        flexGrow: 1,
    },
}));

const FormSection = ({
    backButton,
    nextButton,
    children,
    schema = {},
    initialValues = {},
    validateOnChange,
    gridify = true,
}) => {
    const looper = useContext(LoopContext);
    const loopFieldHelper = useFormLoopPrefix();

    let sectionSchema = {};
    let sectionValues = {};

    // Init section with current loop data
    if (looper) {
        if (looper.parent) {
            sectionSchema = {
                [looper.parent.node]: Joi.array().items(
                    Joi.object({
                        [looper.node]: Joi.array().items(
                            Joi.object(schema).unknown(true)
                        ),
                    }).unknown(true)
                ),
            };
        } else {
            sectionSchema = {
                [looper.node]: Joi.array().items(
                    Joi.object(schema).unknown(true)
                ),
            };
        }

        sectionValues = Object.keys(initialValues).reduce((object, key) => {
            return {
                ...object,
                [loopFieldHelper(key)]: initialValues[key],
            };
        }, {});
    } else {
        // Not looping, continue as normal
        sectionSchema = schema;
        sectionValues = initialValues;
    }

    const classes = useStyles();
    const methods = useFormMethods({
        schema: sectionSchema,
        initialValues: sectionValues,
    });
    const interceptor = useContext(StateSaveInterceptor);

    const values = methods.watch(validateOnChange);

    // RHF doesn't really expose a way to trigger validations on other fields
    // So let's fake this by triggering all validations for passed fields, when
    // the value changes. Stringify here to avoid array-creation resulting in
    // always-new and throwing us into an infinite validation loop.
    useEffect(() => {
        if (validateOnChange) {
            methods.trigger();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(values), validateOnChange]);

    const {node} = useContext(FormState);
    const {updateState} = useFormPersistence();
    const {setBackButton, setNextButton} = useContext(FormNavigation);

    const form = useRef(null);

    const saveAndReturnLatest = () => {
        const data = methods.getValues();
        updateState(interceptor ? interceptor(data) : data);

        return getCurrentState(`forms[${node}]`);
    };

    const sectionBack = {
        onClick: () => {
            return saveAndReturnLatest();
        },
    };

    const sectionNext = {
        onClick: async () => {
            if (await methods.trigger()) {
                return saveAndReturnLatest();
            } else {
                console.debug('errors', methods.errors);
                return false;
            }
        },
    };

    useEffect(() => {
        setBackButton &&
            setBackButton({
                ...defaultBack,
                ...sectionBack,
                ...backButton,
            });
        setNextButton &&
            setNextButton({
                ...defaultNext,
                ...sectionNext,
                ...nextButton,
            });
        return () => {
            setBackButton && setBackButton(defaultBack);
            setNextButton && setNextButton(defaultNext);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextButton, backButton]);

    const persistData = data => {
        updateState(interceptor ? interceptor(data) : data);
    };

    // Update latest URL on mount to avoid restore to older pages
    useEffect(() => {
        updateState({
            _lastUpdated: new Date(),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deepNameTransform = (children, layer = 0) => {
        return React.Children.map(children, (child, i) => {
            if (React.isValidElement(child)) {
                if (gridify) {
                    return (
                        <Grid item key={`child-${layer}-${i}`}>
                            {React.cloneElement(child, {
                                name: loopFieldHelper(child.props.name),
                                children: deepNameTransform(
                                    child.props.children,
                                    layer + 1
                                ),
                            })}
                        </Grid>
                    );
                } else {
                    return React.cloneElement(child, {
                        name: loopFieldHelper(child.props.name),
                        children: deepNameTransform(
                            child.props.children,
                            layer + 1
                        ),
                    });
                }
            } else {
                return child;
            }
        });
    };

    return (
        <FormProvider {...methods} loop-index={looper?.index}>
            <form
                className={classes.root}
                onSubmit={methods.handleSubmit(persistData)}
                ref={form}
            >
                <Grid
                    container
                    direction='column'
                    justifyContent='flex-start'
                    align-items='stretch'
                    spacing={2}
                >
                    {deepNameTransform(children)}
                </Grid>
            </form>
        </FormProvider>
    );
};

export default FormSection;
